import { createContext, useContext, useState, ReactNode, useMemo, useEffect, useRef, useCallback } from "react";
import { SettingsCategory, SettingsData, Subcategory } from "../constants/settingsStructure";
import BryntumContext from "@/react/context/bryntumContext";
import { useSettings } from "../hooks/useSettings";
import settingsTemplate from "../constants/settingsTemplate.json";
import { IAuthUser } from "@/types/AuthUser";
import { VIEW_TYPES } from "@/other/constants/OtherKeys";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import useViews from "@/react/hooks/useViews";

// AI Prompt category IDs that should be grouped together
const AI_PROMPT_CATEGORY_IDS = [
  "siteSafetyPrompt",
  "workmanshipPrompt",
  "analyzePrompt",
  "executePrompt",
  "reviewPrompt"
];

export interface SubcategoryWithCategory {
  subcategory: Subcategory;
  category: SettingsCategory;
}

interface SettingsContextType extends ReturnType<typeof useSettings> {
  categories: SettingsCategory[];
  selectedCategory: SettingsCategory | null;
  setSelectedCategory: (category: SettingsCategory | null) => void;
  selectedSubcategory: SubcategoryWithCategory | null;
  setSelectedSubcategory: (item: SubcategoryWithCategory | null) => void;
  isDataManagementOpen: boolean;
  setIsDataManagementOpen: (open: boolean) => void;
  regularCategories: SettingsCategory[];
  aiPromptCategories: SettingsCategory[];
  handleOpenLegacySettings: () => void;
  csvData: SettingsData | null;
  templateData: SettingsData;
  isPreviewMode: boolean;
  user: IAuthUser;
  setNavigationGuard: (guard: (() => Promise<boolean>) | null) => void;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

interface SettingsProviderProps {
  children: ReactNode;
  previewData?: SettingsData | null;
  isPreviewMode?: boolean;
}

export const SettingsProvider = ({
  children,
  previewData,
  isPreviewMode = false
}: SettingsProviderProps) => {
  const viewManagerState = useViews();
  const [csvData, setCsvData] = useState<SettingsData | null>(previewData || null);
  const [isDataManagementOpen, setIsDataManagementOpen] = useState(false);
  const { user } = useContext(BryntumContext);
  const { viewManager } = useSwivuBryntumContext();

  // Use the useSettings hook - get all properties
  const settingsHook = useSettings();
  const { saveSettings, getSettings: fetchSettings, getCategories } = settingsHook;

  // Template JSON is already grouped by IntentAreaId
  const templateData = useMemo(() => {
    return settingsTemplate as unknown as SettingsData;
  }, []);

  // Load settings from localStorage on mount (only if not in preview mode)
  useEffect(() => {
    if (!isPreviewMode) {
      const loadSettings = async () => {
        const data = await fetchSettings();
        setCsvData(data);
      };
      loadSettings();
    }
  }, [fetchSettings, isPreviewMode]);

  // Update csvData when previewData changes
  useEffect(() => {
    if (isPreviewMode && previewData) {
      setCsvData(previewData);
    }
  }, [previewData, isPreviewMode]);

  // Get categories: use CSV data if available, otherwise use template data
  const categories = useMemo(() => {
    return getCategories(csvData, templateData);
  }, [csvData, templateData]);

  const [selectedCategory, setSelectedCategory] = useState<SettingsCategory | null>(
    categories.find(c => c.id === "general") || categories[0] || null
  );

  const [selectedSubcategory, setSelectedSubcategory] = useState<SubcategoryWithCategory | null>(null);
  const prevSelectedCategoryIdRef = useRef<string | null>(null);

  // Reset selections when csvData changes (e.g., after save, import, or initial load)
  useEffect(() => {
    if (categories.length > 0) {
      const newCategory = categories.find(c => c.id === "general") || categories[0] || null;
      setSelectedCategory(newCategory);
      setSelectedSubcategory(null); // Will be set by the next useEffect
    }
  }, [csvData, categories]);

  // Update selected category when categories change
  useEffect(() => {
    if (categories.length > 0 && !selectedCategory) {
      setSelectedCategory(categories.find(c => c.id === "general") || categories[0] || null);
    }
  }, [categories, selectedCategory]);

  // Automatically select first subcategory when sidebar category changes (not on every subcategory click)
  useEffect(() => {
    const otherParams = viewManagerState?.selectedView?.otherParams;
    if (otherParams?.settingSubcategoryId || otherParams?.setting) return;

    const categoryId = selectedCategory?.id ?? null;
    const categoryChanged = prevSelectedCategoryIdRef.current !== categoryId;
    prevSelectedCategoryIdRef.current = categoryId;

    if (selectedCategory && selectedCategory.subcategories.length > 0) {
      if (categoryChanged || !selectedSubcategory) {
        const firstSubcategory = selectedCategory.subcategories[0];
        setSelectedSubcategory({
          subcategory: firstSubcategory,
          category: selectedCategory
        });
      }
    }
  }, [selectedCategory, selectedSubcategory, viewManagerState?.selectedView?.otherParams]);

  // When opening Settings with otherParams, select the target subcategory and its category
  useEffect(() => {
    const selectedView = viewManagerState.selectedView;
    if (!selectedView.otherParams || categories.length === 0) return;

    const { settingSubcategoryId, setting: settingId } = selectedView.otherParams;

    if (settingSubcategoryId) {
      for (const category of categories) {
        const subcategory = category.subcategories.find((s) => s.id === settingSubcategoryId);
        if (subcategory) {
          setSelectedCategory(category);
          setSelectedSubcategory({ subcategory, category });
          return;
        }
      }
    }

    if (settingId) {
      for (const category of categories) {
        const subcategory = category.subcategories.find((s) => s.settingId === settingId);
        if (subcategory) {
          setSelectedCategory(category);
          setSelectedSubcategory({ subcategory, category });
          break;
        }
      }
    }
  }, [viewManagerState, categories]);

  // Separate regular categories from AI prompt categories
  const regularCategories = useMemo(
    () => categories.filter(c => !AI_PROMPT_CATEGORY_IDS.includes(c.id)),
    [categories]
  );

  const aiPromptCategories = useMemo(
    () => categories.filter(c => AI_PROMPT_CATEGORY_IDS.includes(c.id)),
    [categories]
  );

  const navigationGuardRef = useRef<(() => Promise<boolean>) | null>(null);

  const setNavigationGuard = useCallback((guard: (() => Promise<boolean>) | null) => {
    navigationGuardRef.current = guard;
  }, []);

  const guardedSetSelectedCategory = useCallback(async (category: SettingsCategory | null) => {
    if (navigationGuardRef.current) {
      const canProceed = await navigationGuardRef.current();
      if (!canProceed) return;
    }
    setSelectedCategory(category);
  }, []);

  const guardedSetSelectedSubcategory = useCallback(async (item: SubcategoryWithCategory | null) => {
    if (navigationGuardRef.current) {
      const canProceed = await navigationGuardRef.current();
      if (!canProceed) return;
    }
    setSelectedSubcategory(item);
  }, []);

  const handleOpenLegacySettings = () => {
    if (viewManager) {
      viewManager.openAnonymousViewByType({ viewType: VIEW_TYPES.settings, viewName: "Settings" });
    }
  };

  // Wrapper for getSettings that also updates local state
  const getSettings = async (): Promise<SettingsData> => {
    const data = await fetchSettings();
    setCsvData(data);
    return data;
  };

  const value: SettingsContextType = {
    ...settingsHook,
    categories,
    selectedCategory,
    setSelectedCategory: guardedSetSelectedCategory,
    selectedSubcategory,
    setSelectedSubcategory: guardedSetSelectedSubcategory,
    isDataManagementOpen,
    setIsDataManagementOpen,
    regularCategories,
    aiPromptCategories,
    handleOpenLegacySettings,
    setNavigationGuard,
    saveSettings: async (data: SettingsData) => {
      await saveSettings(data);
      setCsvData(data);
    },
    getSettings,
    csvData,
    templateData,
    isPreviewMode,
    user: user as unknown as IAuthUser,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettingsContext = () => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error("useSettingsContext must be used within a SettingsProvider");
  }
  return context;
};
